import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Public VAPID key — safe to ship to the browser. Pair lives in Supabase secrets.
export const VAPID_PUBLIC_KEY =
  'BPjlgNCUBjzGnpKxPqYrVUICHiqtjnevVo-yMqIwHsEZDFGdQmzJO4326Mo5JSHPXmpuduWZRIg-TtqfHocvpjs';

export const usePushNotifications = (userEmail?: string) => {
  const [isSupported, setIsSupported] = useState(false);
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const getAuthUserId = async (): Promise<string | null> => {
    const { data } = await supabase.auth.getUser();
    return data.user?.id ?? null;
  };

  const saveSubscriptionToDatabase = useCallback(
    async (sub: PushSubscription) => {
      const userId = await getAuthUserId();
      if (!userId) return;

      const p256dh = sub.getKey('p256dh');
      const auth = sub.getKey('auth');
      const { error } = await (supabase as any)
        .from('user_push_subscriptions')
        .upsert(
          {
            user_id: userId,
            user_email: userEmail ?? '',
            endpoint: sub.endpoint,
            p256dh: p256dh ? btoa(String.fromCharCode(...new Uint8Array(p256dh))) : null,
            auth: auth ? btoa(String.fromCharCode(...new Uint8Array(auth))) : null,
            user_agent: navigator.userAgent,
            is_active: true,
            last_seen_at: new Date().toISOString(),
          },
          { onConflict: 'user_email,endpoint' },
        );
      if (error) console.error('Failed to save subscription:', error);
    },
    [userEmail],
  );

  const checkExistingSubscription = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();
      if (existing) {
        setSubscription(existing);
        setIsSubscribed(true);
        await saveSubscriptionToDatabase(existing);
      }
    } catch (error) {
      console.error('Error checking existing subscription:', error);
    }
  }, [saveSubscriptionToDatabase]);

  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    if (supported && userEmail) checkExistingSubscription();
  }, [userEmail, checkExistingSubscription]);

  const requestPermission = async (): Promise<boolean> => {
    if (!('Notification' in window)) return false;
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  };

  const subscribe = async (): Promise<boolean> => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in this browser');
      return false;
    }
    try {
      if (!(await requestPermission())) {
        toast.error('Notification permission denied');
        return false;
      }
      const registration = await navigator.serviceWorker.ready;
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
      });
      await saveSubscriptionToDatabase(pushSubscription);
      setSubscription(pushSubscription);
      setIsSubscribed(true);
      toast.success('Push notifications enabled');
      return true;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      toast.error('Failed to enable push notifications');
      return false;
    }
  };

  const unsubscribe = async (): Promise<boolean> => {
    if (!subscription) return false;
    try {
      await subscription.unsubscribe();
      await (supabase as any)
        .from('user_push_subscriptions')
        .update({ is_active: false })
        .eq('endpoint', subscription.endpoint);
      setSubscription(null);
      setIsSubscribed(false);
      toast.success('Push notifications disabled');
      return true;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      toast.error('Failed to disable push notifications');
      return false;
    }
  };

  return { isSupported, isSubscribed, subscribe, unsubscribe, requestPermission };
};

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}
